<template>
  <div class="h-screen overflow-hidden flex flex-col bg-white border-solid border-l border-0 border-gray-border">
    <div class="border-solid border-b border-0 border-gray-border px-8 pb-8">
      <input-with-error-message v-if="isRenameMode"
                                id="index-editor-name-input"
                                :value.sync="botName"
                                :is-loading="isLoading"
                                @escape="$emit('set-rename-mode', false)"
                                @submit="$emit('rename',{ ...bot, name: $event })"
                                class="h-full mt-5 w-2/3"
      />
      <div v-else class="mt-8 inline-block pr-14 relative">
        <h3 class="font-semibold">
          {{ bot.name }}
        </h3>
        <side-list-menu v-if="!isRenameMode"
                        :items="menuItems"
                        class="absolute top-0 right-0 -mt-1"
                        @rename="$emit('set-rename-mode', true)"
                        @convert-to-multi-market="$emit('request-convert-to-multi-market', bot)"
                        @delete="$emit('request-delete', bot)"
        />
      </div>
    </div>

    <div class="h-full flex flex-col justify-between overflow-y-auto">
      <div class="mt-8 px-8 flex flex-col gap-6 text-gray-dark">
        <div>
          <label class="font-bold">Bot Type</label>
          <div class="mt-1 flex items-center gap-2">
            <span>{{ formatBotType(bot.type) }}</span>
            <vs-icon :icon="getIcon(bot.type)"/>
          </div>
        </div>

        <div>
          <label class="font-bold">Language</label>
          <div class="mt-1 flex items-center gap-2">
            <span>{{ getBotLanguage(bot.language) }}</span>
            <vs-icon icon="language"/>
          </div>
        </div>

        <div v-if="showUnreleased">
          <base-label id="intentRecognition"
                      label="Intent recognition"
                      :is-admin="true"
          />
          <base-select :value="bot.nlp_model_type"
                       :options="nlpProviderOptions"
                       :disabled="isLoading || !isNlpProviderEditable"
                       class="mt-1 w-2/3"
                       @input="onNlpProviderChange"
          />
          <p v-if="isMultiBotPrimary" class="mt-1 text-sm text-gray-dark">Applies to all market variants of this bot.</p>
        </div>

        <!-- PRO-671: like the NLP provider above, this is a setting of the LOGICAL bot, so it is edited on a
             standalone bot or on the multi-bot's primary — never per market variant. -->
        <settings-bot-agent v-if="showUnreleased && isNlpProviderEditable" :bot="bot" class="pb-8"/>
      </div>
    </div>
  </div>
</template>

<script>
import BaseLabel from '@/components/base-components/BaseLabel.vue'
import InputWithErrorMessage from '@/components/common/InputWithErrorMessage.vue'
import SettingsBotAgent from '@/components/settings/bots/SettingsBotAgent.vue'
import SideListMenu from '@/components/common/SideListMenu'
import { BOT_LANGUAGES } from '@/utils/bot.constants'
import { MENU_ITEMS } from '@/utils/editor.constants'

export default {
  name: 'SettingsBotSummary',

  components: {
    BaseLabel,
    InputWithErrorMessage,
    SettingsBotAgent,
    SideListMenu
  },

  props: {
    bot: {
      type: Object,
      required: true
    },
    isLoading: {
      type: Boolean,
      default: false
    },
    isRenameMode: {
      type: Boolean,
      default: false
    }
  },

  watch: {
    bot: {
      immediate: true,
      handler(bot) {
        this.botName = bot.name
      }
    }
  },

  data() {
    return {
      botName: ''
    }
  },

  computed: {
    menuItems() {
      if (['111', '222'].includes(this.bot.id)) {
        return [MENU_ITEMS.rename]
      }
      const items = [MENU_ITEMS.rename]
      if (!this.bot.multi_bot_id) {
        items.push(MENU_ITEMS.convertToMultiMarket)
      }
      items.push(MENU_ITEMS.delete)
      return items
    },
    // provider is a group-wide setting — editable only on a standalone bot or a multi-bot's primary
    isNlpProviderEditable() {
      return !this.bot.multi_bot_id || this.bot.is_primary_variant === true
    },
    isMultiBotPrimary() {
      return !!this.bot.multi_bot_id && this.bot.is_primary_variant === true
    },
    nlpProviderOptions() {
      return [
        { value: 'gpt', text: 'GPT (prompt)' },
        { value: 'gpt-vector', text: 'GPT Vector (semantic)' },
        { value: 'gpt-slug', text: 'GPT Slug (prompt, readable ids)' },
        // PRO-705: same intent list as `gpt`, classified by TypeSafe Jev instead of the Azure deployment.
        // Nothing else about the bot changes, so switching back is picking GPT again.
        { value: 'jev', text: 'TypeSafe Jev (internal)' }
      ]
    }
  },

  methods: {
    onNlpProviderChange(provider) {
      if (provider && provider !== this.bot.nlp_model_type) this.$emit('change-nlp-provider', provider)
    },
    formatBotType(botType) {
      return botType[0].toUpperCase() + botType.slice(1)
    },
    getIcon(type) {
      return type === 'chat' ? 'message' : 'email_outline'
    },
    getBotLanguage(languageValue) {
      return BOT_LANGUAGES.find(language => language.value === languageValue)?.text
    }
  }
}
</script>
